import type { MeView } from '@research-assistant/shared';
import {
  BugIcon,
  ChevronUpIcon,
  HeartIcon,
  LightbulbIcon,
  MessageSquarePlusIcon,
} from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.js';
import { BugReportButton } from '@/features/bug-report/BugReportButton.js';
import { GeneralFeedbackDialog } from '@/features/feedback/GeneralFeedbackDialog.js';
import { FeatureRequestDialog } from '@/features/home/FeatureRequestBanner.js';
import { useSupportTicketsEnabled } from '@/lib/api/support.js';
import {
  DEFAULT_FEEDBACK_URL,
  getRuntimeConfig,
} from '@/lib/runtime-config.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

export interface FeedbackLauncherProps {
  identity: MeView | null;
  workspaceId: string | null;
  conversationId: string | null;
}

/** Global, explicit entry point for both kinds of product feedback. */
export function FeedbackLauncher({
  identity,
  workspaceId,
  conversationId,
}: FeedbackLauncherProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
  const [generalFeedbackOpen, setGeneralFeedbackOpen] = useState(false);
  const canFileTickets = useSupportTicketsEnabled();
  const FeatureAction = canFileTickets ? 'button' : 'a';
  const feedbackUrl =
    safeHttpUrl(getRuntimeConfig().feedbackUrl) || DEFAULT_FEEDBACK_URL;

  return (
    <BugReportButton
      identity={identity}
      workspaceId={workspaceId}
      conversationId={conversationId}
      renderTrigger={(openBugReport) => (
        <>
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                aria-label="Feedback"
                className="mb-3 mr-3 h-9 shrink-0 self-end gap-2 rounded-full border-border/80 bg-card/95 px-3 text-foreground shadow-elev-2 backdrop-blur-md transition-all hover:bg-card hover:shadow-elev-3 md:absolute md:bottom-3 md:right-3 md:z-30 md:mb-0 md:mr-0"
              >
                <MessageSquarePlusIcon className="size-4" aria-hidden />
                <span>Feedback</span>
                <ChevronUpIcon
                  className="size-3.5 text-muted-foreground"
                  aria-hidden
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="end"
              sideOffset={8}
              className="w-60 p-1.5"
            >
              <div role="menu" aria-label="Feedback options">
                <button
                  type="button"
                  role="menuitem"
                  aria-label="Report a bug"
                  onClick={() => {
                    setMenuOpen(false);
                    openBugReport();
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
                    <BugIcon className="size-4" aria-hidden />
                  </span>
                  <span>
                    <span className="block font-medium text-foreground">
                      Report a bug
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Something is not working
                    </span>
                  </span>
                </button>
                <FeatureAction
                  {...(canFileTickets
                    ? { type: 'button' as const }
                    : {
                        href: feedbackUrl,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                      })}
                  role="menuitem"
                  aria-label="Request a feature"
                  onClick={() => {
                    setMenuOpen(false);
                    if (canFileTickets) setFeatureRequestOpen(true);
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <LightbulbIcon className="size-4" aria-hidden />
                  </span>
                  <span>
                    <span className="block font-medium text-foreground">
                      Request a feature
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Share an idea with the team
                    </span>
                  </span>
                </FeatureAction>
                <button
                  type="button"
                  role="menuitem"
                  aria-label="Share feedback"
                  onClick={() => {
                    setMenuOpen(false);
                    setGeneralFeedbackOpen(true);
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400">
                    <HeartIcon className="size-4" aria-hidden />
                  </span>
                  <span>
                    <span className="block font-medium text-foreground">
                      Share feedback
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      Tell us about your experience
                    </span>
                  </span>
                </button>
              </div>
            </PopoverContent>
          </Popover>

          <FeatureRequestDialog
            open={featureRequestOpen}
            onOpenChange={setFeatureRequestOpen}
          />
          <GeneralFeedbackDialog
            open={generalFeedbackOpen}
            onOpenChange={setGeneralFeedbackOpen}
          />
        </>
      )}
    />
  );
}
